import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Camera, Loader2, AlertCircle, Globe, Check, X, Phone, ArrowLeft } from 'lucide-react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useLogin, useRegister, useRequestOtp, useVerifyOtp, useGoogleLogin } from '@/api/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { Navigate } from 'react-router-dom';
import { SUPPORTED_LANGUAGES } from '@/i18n/i18n';
import type { SupportedLanguage } from '@/i18n/i18n';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

/* ---------- Validation Schemas ---------- */

const loginSchema = z.object({
  identifier: z.string().min(1, 'auth.validation.identifierRequired'),
  password: z.string().min(1, 'auth.validation.passwordRequired'),
});

const PASSWORD_RULES = [
  { key: 'minLength', test: (p: string) => p.length >= 8 },
  { key: 'notAllNumeric', test: (p: string) => !/^\d+$/.test(p) },
  { key: 'hasLetter', test: (p: string) => /[a-zA-Z]/.test(p) },
  { key: 'notCommon', test: (p: string) => !['password', '12345678', 'qwerty12', 'abcd1234', 'password1'].includes(p.toLowerCase()) },
] as const;

const registerSchema = z
  .object({
    email: z.string().min(1, 'auth.validation.emailRequired').email('auth.validation.emailInvalid'),
    first_name: z.string().min(1, 'auth.validation.firstNameRequired'),
    last_name: z.string().min(1, 'auth.validation.lastNameRequired'),
    phone_number: z.string().optional(),
    studio_name: z.string().optional(),
    password: z
      .string()
      .min(8, 'auth.validation.passwordMin')
      .refine((p) => !/^\d+$/.test(p), 'auth.validation.passwordNotAllNumeric')
      .refine((p) => /[a-zA-Z]/.test(p), 'auth.validation.passwordNeedsLetter'),
    password_confirm: z.string().min(1, 'auth.validation.confirmPasswordRequired'),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'auth.validation.passwordsMismatch',
    path: ['password_confirm'],
  });

type LoginFormData = z.infer<typeof loginSchema>;
type RegisterFormData = z.infer<typeof registerSchema>;

type PageMode = 'login' | 'register' | 'phone' | 'otp-verify';

/* ---------- Component ---------- */

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<PageMode>('login');
  const [otpUserId, setOtpUserId] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const switchMode = (newMode: PageMode) => {
    setMode(newMode);
  };

  const handleOtpRequested = (userId: string, phone: string) => {
    setOtpUserId(userId);
    setOtpPhone(phone);
    setMode('otp-verify');
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return t('auth.loginTitle');
      case 'register': return t('auth.registerTitle');
      case 'phone': return t('auth.phoneLogin');
      case 'otp-verify': return t('auth.otpTitle');
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'login': return t('auth.loginSubtitle');
      case 'register': return t('auth.registerSubtitle');
      case 'phone': return t('auth.phoneLoginSubtitle');
      case 'otp-verify': return t('auth.otpSubtitle');
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-surface">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 shadow-lg shadow-primary-500/25">
              <Camera className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {getTitle()}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
              {getSubtitle()}
            </p>
          </div>

          {/* Form card */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-surface-border dark:bg-surface-card dark:shadow-2xl dark:shadow-black/40">
            {mode === 'login' && (
              <LoginForm onSwitchToPhone={() => switchMode('phone')} />
            )}
            {mode === 'register' && <RegisterForm />}
            {mode === 'phone' && (
              <PhoneForm
                onOtpRequested={handleOtpRequested}
                onBack={() => switchMode('login')}
              />
            )}
            {mode === 'otp-verify' && (
              <OtpVerifyForm
                userId={otpUserId}
                phone={otpPhone}
                onBack={() => switchMode('login')}
              />
            )}

            {/* Toggle login/register */}
            {(mode === 'login' || mode === 'register') && (
              <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-600">
                {mode === 'login' ? (
                  <>
                    {t('auth.noAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('register')}
                      className="font-semibold text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {t('auth.register')}
                    </button>
                  </>
                ) : (
                  <>
                    {t('auth.hasAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="font-semibold text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {t('auth.login')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Language switcher */}
          <div className="mt-6 flex items-center justify-center gap-2">
            <Globe className="h-4 w-4 text-gray-400" />
            {SUPPORTED_LANGUAGES.map((lng, idx) => (
              <span key={lng} className="flex items-center gap-2">
                {idx > 0 && <span className="text-gray-300 dark:text-gray-700">|</span>}
                <button
                  type="button"
                  onClick={() => i18n.changeLanguage(lng)}
                  className={`text-sm transition-colors ${
                    i18n.language === lng
                      ? 'font-semibold text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 hover:text-gray-800 dark:text-gray-600 dark:hover:text-gray-400'
                  }`}
                >
                  {t(`language.${lng as SupportedLanguage}`)}
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}

/* ---------- Login Form ---------- */

function LoginForm({ onSwitchToPhone }: { onSwitchToPhone: () => void }) {
  const { t } = useTranslation();
  const loginMutation = useLogin();
  const googleLoginMutation = useGoogleLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  const onSubmit = (data: LoginFormData) => {
    const isEmail = data.identifier.includes('@');
    loginMutation.mutate(
      isEmail
        ? { email: data.identifier, password: data.password }
        : { phone_number: data.identifier, password: data.password },
    );
  };

  const handleGoogleSuccess = useCallback((credentialResponse: { credential?: string }) => {
    if (credentialResponse.credential) {
      googleLoginMutation.mutate(credentialResponse.credential);
    }
  }, [googleLoginMutation]);

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Server error */}
        {loginMutation.isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{t('auth.invalidCredentials')}</span>
          </div>
        )}

        {/* Email or Phone */}
        <div>
          <label htmlFor="login-identifier" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-500">
            {t('auth.emailOrPhone')}
          </label>
          <input
            id="login-identifier"
            type="text"
            autoComplete="email tel"
            className={`input-field ${errors.identifier ? '!border-red-500 focus:!ring-red-500' : ''}`}
            placeholder={t('auth.emailOrPhonePlaceholder')}
            {...register('identifier')}
          />
          {errors.identifier && (
            <p className="mt-1 text-xs text-red-400">{t(errors.identifier.message!)}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-500">
            {t('auth.password')}
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            className={`input-field ${errors.password ? '!border-red-500 focus:!ring-red-500' : ''}`}
            {...register('password')}
          />
          {errors.password && (
            <p className="mt-1 text-xs text-red-400">{t(errors.password.message!)}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loginMutation.isPending}
          className="btn-primary w-full"
        >
          {loginMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t('auth.login')
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-surface-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500 dark:bg-surface-card dark:text-gray-600">
            {t('auth.orContinueWith')}
          </span>
        </div>
      </div>

      {/* Google SSO */}
      {GOOGLE_CLIENT_ID && (
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => googleLoginMutation.reset()}
            size="large"
            width="100%"
            text="signin_with"
          />
        </div>
      )}

      {/* Phone login */}
      <button
        type="button"
        onClick={onSwitchToPhone}
        className="btn-secondary w-full flex items-center justify-center gap-2"
      >
        <Phone className="h-4 w-4" />
        {t('auth.phoneLogin')}
      </button>
    </div>
  );
}

/* ---------- Phone Form ---------- */

function PhoneForm({
  onOtpRequested,
  onBack,
}: {
  onOtpRequested: (userId: string, phone: string) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const requestOtpMutation = useRequestOtp();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    requestOtpMutation.mutate(phone, {
      onSuccess: (data) => {
        onOtpRequested(data.user_id, phone);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {requestOtpMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t('auth.otpRequestError')}</span>
        </div>
      )}

      <div>
        <label htmlFor="phone-input" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.phoneNumber')}
        </label>
        <input
          id="phone-input"
          type="tel"
          dir="ltr"
          autoComplete="tel"
          className="input-field"
          placeholder={t('auth.phonePlaceholder')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          required
        />
      </div>

      <button
        type="submit"
        disabled={requestOtpMutation.isPending || !phone.trim()}
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {requestOtpMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Phone className="h-4 w-4" />
            {t('auth.callMe')}
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <ArrowLeft className="h-3 w-3" />
        {t('auth.backToLogin')}
      </button>
    </form>
  );
}

/* ---------- OTP Verify Form ---------- */

function OtpVerifyForm({
  userId,
  phone,
  onBack,
}: {
  userId: string;
  phone: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(30);
  const verifyOtpMutation = useVerifyOtp();
  const requestOtpMutation = useRequestOtp();

  const startCooldown = useCallback(() => {
    setCooldown(30);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    verifyOtpMutation.mutate({ userId, code });
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    requestOtpMutation.mutate(phone, {
      onSuccess: () => startCooldown(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {verifyOtpMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t('auth.otpVerifyError')}</span>
        </div>
      )}

      <div>
        <label htmlFor="otp-input" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.verificationCode')}
        </label>
        <input
          id="otp-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          dir="ltr"
          className="input-field text-center text-2xl tracking-[0.5em] font-mono"
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          required
        />
      </div>

      <button
        type="submit"
        disabled={verifyOtpMutation.isPending || code.length !== 6}
        className="btn-primary w-full"
      >
        {verifyOtpMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          t('auth.verify')
        )}
      </button>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('auth.backToLogin')}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0}
          className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {cooldown > 0
            ? t('auth.resendIn', { seconds: cooldown })
            : t('auth.resendCode')}
        </button>
      </div>
    </form>
  );
}

/* ---------- Register Form ---------- */

function RegisterForm() {
  const { t } = useTranslation();
  const registerMutation = useRegister();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      studio_name: '',
      password: '',
      password_confirm: '',
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    registerMutation.mutate({
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number || undefined,
      studio_name: data.studio_name || undefined,
      password: data.password,
      password_confirm: data.password_confirm,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      {registerMutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{t('auth.registerError')}</span>
        </div>
      )}

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="reg-first-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('auth.firstName')}
          </label>
          <input
            id="reg-first-name"
            type="text"
            autoComplete="given-name"
            className={`input-field ${errors.first_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            {...register('first_name')}
          />
          {errors.first_name && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t(errors.first_name.message!)}</p>
          )}
        </div>
        <div>
          <label htmlFor="reg-last-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('auth.lastName')}
          </label>
          <input
            id="reg-last-name"
            type="text"
            autoComplete="family-name"
            className={`input-field ${errors.last_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            {...register('last_name')}
          />
          {errors.last_name && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t(errors.last_name.message!)}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="reg-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.email')}
        </label>
        <input
          id="reg-email"
          type="email"
          autoComplete="email"
          className={`input-field ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          placeholder="you@example.com"
          {...register('email')}
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t(errors.email.message!)}</p>
        )}
      </div>

      {/* Phone number (optional) */}
      <div>
        <label htmlFor="reg-phone" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.phoneNumber')} <span className="text-gray-400">({t('auth.optional')})</span>
        </label>
        <input
          id="reg-phone"
          type="tel"
          autoComplete="tel"
          className="input-field"
          dir="ltr"
          placeholder="+972501234567"
          {...register('phone_number')}
        />
      </div>

      {/* Studio name (optional) */}
      <div>
        <label htmlFor="reg-studio" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.studioName')} <span className="text-gray-400">({t('auth.optional')})</span>
        </label>
        <input
          id="reg-studio"
          type="text"
          className="input-field"
          {...register('studio_name')}
        />
      </div>

      {/* Password */}
      <div>
        <label htmlFor="reg-password" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.password')}
        </label>
        <input
          id="reg-password"
          type="password"
          autoComplete="new-password"
          className={`input-field ${errors.password ? '!border-red-500 focus:!ring-red-500' : ''}`}
          {...register('password')}
        />
        {errors.password && (
          <p className="mt-1 text-xs text-red-400">{t(errors.password.message!)}</p>
        )}
        <PasswordRules password={watch('password')} />
      </div>

      {/* Confirm password */}
      <div>
        <label htmlFor="reg-password-confirm" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('auth.confirmPassword')}
        </label>
        <input
          id="reg-password-confirm"
          type="password"
          autoComplete="new-password"
          className={`input-field ${errors.password_confirm ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          {...register('password_confirm')}
        />
        {errors.password_confirm && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{t(errors.password_confirm.message!)}</p>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={registerMutation.isPending}
        className="btn-primary w-full"
      >
        {registerMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          t('auth.register')
        )}
      </button>
    </form>
  );
}

/* ---------- Password Strength Rules ---------- */

function PasswordRules({ password }: { password: string }) {
  const { t } = useTranslation();
  const value = password || '';

  if (!value) return null;

  return (
    <ul className="mt-2 space-y-1">
      {PASSWORD_RULES.map(({ key, test }) => {
        const passed = test(value);
        return (
          <li
            key={key}
            className={`flex items-center gap-1.5 text-xs transition-colors ${
              passed
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-400 dark:text-gray-500'
            }`}
          >
            {passed ? (
              <Check className="h-3 w-3 shrink-0" />
            ) : (
              <X className="h-3 w-3 shrink-0" />
            )}
            <span>{t(`auth.validation.rules.${key}`)}</span>
          </li>
        );
      })}
    </ul>
  );
}
